// src/lib/supabase/client.ts
import { createClient } from '@supabase/supabase-js';
import { logger, LOG_CONTEXTS } from '@/utils/logger';
import type { Database } from '../database.types';

// Singleton pattern for Supabase client
let supabaseInstance: ReturnType<typeof createClient<Database>> | null = null;
let isInitialized = false;

// Function to get the Supabase client instance
function getSupabaseClient() {
  if (supabaseInstance) {
    return supabaseInstance;
  }

  const supabaseUrl = import.meta.env.VITE_SUPABASE_URL;
  const supabaseAnonKey = import.meta.env.VITE_SUPABASE_ANON_KEY;

  if (!supabaseUrl || !supabaseAnonKey) {
    throw new Error('Missing Supabase environment variables');
  }

  // Log client initialization
  logger.info(LOG_CONTEXTS.SUPABASE, 'Initializing Supabase client', { 
    url: supabaseUrl, 
    isAlreadyInitialized: isInitialized 
  });

  if (isInitialized) {
    logger.warn(LOG_CONTEXTS.SUPABASE, 'Supabase client already initialized - potential duplicate instance');
  }

  // Create the client
  const originalClient = createClient<Database>(supabaseUrl, supabaseAnonKey, {
    auth: {
      persistSession: true,
      autoRefreshToken: true,
      detectSessionInUrl: true
    }
  });

  isInitialized = true;
  supabaseInstance = originalClient;
  
  return originalClient;
}

// Create a proxy to intercept and log all Supabase operations
export const supabase = new Proxy({} as ReturnType<typeof createClient<Database>>, {
  get(_, prop, receiver) {
    const client = getSupabaseClient();
    const value = Reflect.get(client, prop, receiver);
    
    // If the property is a function, wrap it to add logging
    if (typeof value === 'function') {
      return function(this: any, ...args: any[]) {
        const requestId = logger.createRequestId();
        logger.debug(LOG_CONTEXTS.SUPABASE, `Method called: ${String(prop)}`, { args }, requestId);
        
        const result = value.apply(this === receiver ? client : this, args);
        
        // If the result is a Promise, add logging for resolution
        if (result instanceof Promise) {
          return result.then((data) => {
            logger.debug(LOG_CONTEXTS.SUPABASE, `Method resolved: ${String(prop)}`, { data }, requestId);
            return data;
          }).catch((error) => {
            logger.error(LOG_CONTEXTS.SUPABASE, `Method error: ${String(prop)}`, { error }, requestId);
            throw error;
          });
        }
        
        // If the result is an object with chainable methods (like query builder)
        if (result && typeof result === 'object') {
          return new Proxy(result, {
            get(obj, methodProp, methodReceiver) {
              const methodValue = Reflect.get(obj, methodProp, methodReceiver);
              
              if (typeof methodValue === 'function') {
                return function(this: any, ...methodArgs: any[]) {
                  logger.debug(
                    LOG_CONTEXTS.SUPABASE, 
                    `Chained method called: ${String(prop)}.${String(methodProp)}`, 
                    { args: methodArgs }, 
                    requestId
                  );
                  
                  const methodResult = methodValue.apply(this, methodArgs);
                  
                  // Handle promises in chained methods
                  if (methodResult instanceof Promise) {
                    return methodResult.then((data) => {
                      logger.debug(
                        LOG_CONTEXTS.SUPABASE, 
                        `Chained method resolved: ${String(prop)}.${String(methodProp)}`, 
                        { data }, 
                        requestId
                      );
                      return data;
                    }).catch((error) => {
                      logger.error(
                        LOG_CONTEXTS.SUPABASE, 
                        `Chained method error: ${String(prop)}.${String(methodProp)}`, 
                        { error }, 
                        requestId
                      );
                      throw error;
                    });
                  }
                  
                  return methodResult;
                };
              }
              
              return methodValue;
            }
          });
        }
        
        return result;
      };
    }
    
    // For auth and other objects, create a sub-proxy
    if (value && typeof value === 'object' && ['auth', 'from', 'rpc'].includes(String(prop))) {
      return new Proxy(value, {
        get(obj, methodProp, methodReceiver) {
          const methodValue = Reflect.get(obj, methodProp, methodReceiver);
          
          if (typeof methodValue === 'function') {
            return function(this: any, ...args: any[]) {
              const requestId = logger.createRequestId();
              logger.debug(
                LOG_CONTEXTS.SUPABASE, 
                `Sub-method called: ${String(prop)}.${String(methodProp)}`, 
                { args }, 
                requestId
              );
              
              const result = methodValue.apply(this, args);
              
              // Handle promises
              if (result instanceof Promise) {
                return result.then((data) => {
                  logger.debug(
                    LOG_CONTEXTS.SUPABASE, 
                    `Sub-method resolved: ${String(prop)}.${String(methodProp)}`, 
                    { data }, 
                    requestId
                  );
                  return data;
                }).catch((error) => {
                  logger.error(
                    LOG_CONTEXTS.SUPABASE, 
                    `Sub-method error: ${String(prop)}.${String(methodProp)}`, 
                    { error }, 
                    requestId
                  );
                  throw error;
                });
              }
              
              return result;
            };
          }
          
          return methodValue;
        }
      });
    }
    
    return value;
  }
});

// Helper to get current session
export const getCurrentSession = async () => {
  const requestId = logger.createRequestId();
  logger.debug(LOG_CONTEXTS.SUPABASE, 'Getting current session', {}, requestId);
  
  try {
    const { data: { session }, error } = await supabase.auth.getSession();
    if (error) {
      logger.error(LOG_CONTEXTS.SUPABASE, 'Error getting session', { error }, requestId);
      throw error;
    }
    
    logger.debug(LOG_CONTEXTS.SUPABASE, 'Session retrieved', { 
      hasSession: !!session,
      userId: session?.user?.id 
    }, requestId);
    
    return session;
  } catch (error) {
    logger.error(LOG_CONTEXTS.SUPABASE, 'Exception getting session', { error }, requestId);
    throw error;
  }
};

// Helper to get current user
export const getCurrentUser = async () => {
  const requestId = logger.createRequestId();
  logger.debug(LOG_CONTEXTS.SUPABASE, 'Getting current user', {}, requestId);
  
  try {
    const session = await getCurrentSession();
    const user = session?.user ?? null;
    
    logger.debug(LOG_CONTEXTS.SUPABASE, 'User retrieved', { 
      hasUser: !!user,
      userId: user?.id 
    }, requestId);
    
    return user;
  } catch (error) {
    logger.error(LOG_CONTEXTS.SUPABASE, 'Exception getting user', { error }, requestId);
    throw error;
  }
};

